/* eslint-disable no-alert */
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  faEyeSlash,
  faEnvelope,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import './Login.scss';
import {
  MinimalForm,
  FormInput,
  FormButton,
} from '../../../Components/XpektorForms/XpektorForms';
import XpektorLogo from '../../../Components/XpektorLogo/XpektorLogo';
import AuthService from '../../../Services/AuthService';
import UserService from '../../../Services/UserService';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorText, setErrorText] = useState('');
  const [passwordFieldType, setPasswordFieldType] = useState('password');
  const [loggingIn, setLoggingIn] = useState(false);

  const [sendingForgot, setSendingForgot] = useState(false);
  const [forgotRequestSent, setForgotRequestSent] = useState(false);

  const [action, setAction] = useState('login');

  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('login-page.document-title');
  }, [t, history]);

  const togglePasswordVisibility = () => {
    if (passwordFieldType === 'password') {
      setPasswordFieldType('text');
    } else {
      setPasswordFieldType('password');
    }
  };

  const loginAPI = async () => {
    try {
      setLoggingIn(true);
      const response = await AuthService.login(email, password);
      if (response) {
        history.push('/app/dashboard');
      }
    } catch (error) {
      setLoggingIn(false);
      if (error.response.status === 400) {
        setErrorText(t('login-page.error-message.wrong-user-or-pass'));
      } else if (error.response.status === 500) {
        setErrorText(t('login-page.error-message.something-went-wrong'));
      }
    }
  };
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  function validateLoginForm() {
    return email.length > 0 && password.length > 0;
  }

  function validateForgotForm() {
    return email.length > 0;
  }

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    if (!emailRegex.test(email) || !validateLoginForm()) {
      setErrorText(t('login-page.error-message.wrong-user-or-pass'));
      return;
    }

    try {
      localStorage.clear();
      await loginAPI();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleForgotSubmit = async (event) => {
    event.preventDefault();

    if (!emailRegex.test(email)) {
      setErrorText(t('login-page.error-message.invalid-email'));
      return;
    }

    setSendingForgot(true);
    await UserService.ForgotPassword(email)
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        setForgotRequestSent(true);
        setErrorText('');
        setSendingForgot(false);
      });
  };

  return (
    <div className="login-wrapper">
      <div className="image-column">
        <XpektorLogo darkMode />
      </div>
      <div className="login-column">
        <div className="login-header">
          {action === 'login' ? (
            <>
              <p className="login-title">{t('shared.sign-in')}</p>
              <p className="sub-title">{t('login-page.sub-title')}</p>
            </>
          ) : (
            <p className="forgot-title">{t('login-page.forgot-title')}</p>
          )}
        </div>
        <div className="login-form-container">
          {action === 'login' ? (
            <MinimalForm onSubmit={handleLoginSubmit}>
              <FormInput
                label={t('shared.email')}
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={faEnvelope}
              />
              <FormInput
                label={t('shared.password')}
                type={passwordFieldType}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={passwordFieldType === 'password' ? faEyeSlash : faEye}
                onIconClick={() => {
                  togglePasswordVisibility();
                }}
                infoText={errorText}
              />
              <FormButton
                text={loggingIn ? t('shared.signing-in') : t('shared.sign-in')}
              />
            </MinimalForm>
          ) : (
            <>
              <p className="forgot-password-description">
                {t('login-page.forgot-pass-description')}
              </p>
              <MinimalForm onSubmit={handleForgotSubmit}>
                <FormInput
                  label={t('shared.email')}
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={faEnvelope}
                  infoText={
                    !errorText
                      ? forgotRequestSent && t('login-page.send-text.sent')
                      : errorText
                  }
                  infoOk={!errorText}
                />
                <FormButton
                  text={
                    sendingForgot
                      ? t('login-page.send-text.sending')
                      : t('login-page.send-text.send')
                  }
                  disabled={!validateForgotForm() || sendingForgot}
                />
              </MinimalForm>
            </>
          )}
          <button
            className="forgot-password-button"
            type="button"
            onClick={() => {
              setAction(action === 'login' ? 'forgot-password' : 'login');
              setPassword('');
              setErrorText('');
            }}
          >
            <p>
              {action === 'login'
                ? t('login-page.forgot-pass-btn.forgot-pass-q')
                : t('login-page.forgot-pass-btn.return')}
            </p>
          </button>
        </div>
        <div className="login-footer-container">
          <div className="contact">
            <p>
              {t('shared.want-to-try-xpektor')}&nbsp;
              <a
                href="https://meetings-eu1.hubspot.com/jacob-liebermann?embed=true"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('shared.book-demo')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
